import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, BookImage, Smile, Sparkles } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const STYLES = [
  {
    name: "Book Cover Design",
    base: 229,
    icon: BookImage,
    desc: "Custom book covers designed to capture attention and bring your story to life.",
  },
  {
    name: "Custom Character Design",
    base: 349,
    icon: Smile,
    desc: "Unique characters designed from your ideas for books, comics, games, or branding.",
  },
  {
    name: "Custom Business & Brand Illustration",
    base: 699,
    icon: Sparkles,
    desc: "Custom illustrations for businesses, including website artwork, product packaging, social media graphics, promotional materials, posters, and marketing campaigns.",
  },
];

const LOGO_QTYS = [1, 2, 3];

const priceForQty = (base, qty) => {
  if (qty === 1) return base;
  const qty2 = base + 150;
  if (qty === 2) return qty2;
  return qty2 + 220;
};

const StyleCard = ({ style }) => {
  const [qty, setQty] = useState(1);
  const Icon = style.icon;
  const slug = style.name.toLowerCase().replace(/\s+/g, "-");
  const price = priceForQty(style.base, qty);

  return (
    <div
      data-testid={`illustration-style-${slug}`}
      className="brutal-border brutal-shadow bg-white p-7 sm:p-9 flex flex-col"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#EAF1F8] text-[#3E6FA8] flex items-center justify-center">
          <Icon size={26} strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-[#1F2530] leading-none">
            {style.name}
          </h2>
          <p className="text-sm font-semibold uppercase tracking-wider text-[#3E6FA8] mt-1">
            From ${style.base.toLocaleString()} / 1 illustration
          </p>
        </div>
      </div>

      <p className="mt-5 leading-relaxed text-[#1F2530]/70">{style.desc}</p>

      <div className="mt-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60 mb-3">
          Illustration quantity
        </p>
        <div className="flex flex-wrap gap-2">
          {LOGO_QTYS.map((q) => (
            <button
              key={q}
              data-testid={`illustration-qty-${slug}-${q}`}
              onClick={() => setQty(q)}
              className={`min-w-[56px] px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                qty === q
                  ? "bg-[#3E6FA8] text-white border-[#3E6FA8]"
                  : "bg-white text-[#1F2530] border-[#E3E5E9] hover:border-[#3E6FA8] hover:text-[#3E6FA8]"
              }`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-7 pt-6 border-t border-[#E3E5E9] flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60">
            {qty} illustration{qty > 1 ? "s" : ""}
          </p>
          <p
            data-testid={`illustration-price-${slug}`}
            className="inline-block mt-2 text-base font-bold uppercase tracking-wider bg-[#EAF1F8] text-[#3E6FA8] px-3 py-1.5 rounded-full"
          >
            ${price.toLocaleString()}
          </p>
        </div>
        <Link
          to="/contact"
          state={{ service: `Custom Illustrations — ${style.name} (qty ${qty})` }}
          data-testid={`illustration-order-${slug}`}
          className="shrink-0 inline-flex items-center gap-2 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider text-sm px-5 py-3"
        >
          Order <ArrowRight size={18} strokeWidth={2.5} />
        </Link>
      </div>
    </div>
  );
};

export default function CustomIllustrations() {
  return (
    <div className="App">
      <Navbar />

      {/* HEADER */}
      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="custom-illustrations-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#F3F4F6]/70 hover:text-[#7BA7D9] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back home
          </Link>
          <span className="block mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
            Custom Illustrations
          </span>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-3">
            Custom Illustration
          </h1>
          <p className="mt-5 max-w-2xl text-lg font-medium text-[#F3F4F6]/70">
            Choose your illustration, then pick how many illustrations you need to see
            pricing instantly. Book Cover Design starts at{" "}
            <span className="font-bold text-[#7BA7D9]">$229</span>, Custom Character Design at{" "}
            <span className="font-bold text-[#7BA7D9]">$349</span>, and Custom Business & Brand Illustration at{" "}
            <span className="font-bold text-[#7BA7D9]">$699</span>.
          </p>
        </div>
      </section>

      {/* STYLES */}
      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            data-testid="illustration-styles-grid"
          >
            {STYLES.map((s) => (
              <StyleCard key={s.name} style={s} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 brutal-border brutal-shadow bg-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1F2530]">
                Have something else in mind?
              </h2>
              <p className="mt-2 font-medium text-[#1F2530]/70">
                Tell us about your idea and we'll craft a custom illustration just for you.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="custom-illustrations-cta"
              className="shrink-0 inline-flex items-center gap-3 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-7 py-4"
            >
              Start Your Order <ArrowRight size={20} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
